import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Flame, Trophy } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { useMotivationStore } from "../../store/motivationStore";
import { useAuthStore } from "../../store/authStore";
import useTenantWebSocket from "../../hooks/useTenantWebSocket";

/**
 * Compact XP + streak chip for the TopBar. Links to /app/motivasyon.
 * Also listens for `motivation.update` WS events → toast + confetti when
 * the current user completes a task.
 */
export default function MotivationChip() {
  const { user } = useAuthStore();
  const { stats, level, catalog, fetchMe } = useMotivationStore();

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  useTenantWebSocket((msg) => {
    if (!msg) return;
    if (msg.event === "motivation.update" && msg.user_id === user?.id) {
      fetchMe();
      toast.success(`+${msg.xp_gained} XP 🎉`);
      // Basic confetti burst.
      try {
        confetti({
          particleCount: 60,
          spread: 65,
          origin: { x: 0.95, y: 0.1 },
        });
      } catch {
        /* ignore */
      }
      if (Array.isArray(msg.new_badges) && msg.new_badges.length > 0) {
        setTimeout(() => {
          msg.new_badges.forEach((code) => {
            const b = catalog.find((c) => c.code === code);
            toast(
              `🏆 Yeni rozet: ${b?.emoji || ""} ${b?.name || code}`,
              { duration: 5000 },
            );
          });
          try {
            confetti({
              particleCount: 150,
              spread: 120,
              origin: { x: 0.5, y: 0.3 },
            });
          } catch {
            /* ignore */
          }
        }, 300);
      }
    }
    if (msg.event === "motivation.challenge" && msg.user_id === user?.id) {
      fetchMe();
      toast.success(
        `🏆 Meydan okuma tamamlandı: ${msg.title} · +${msg.reward_xp} XP`,
        { duration: 5000 },
      );
      try {
        confetti({
          particleCount: 200,
          spread: 160,
          origin: { x: 0.5, y: 0.4 },
        });
      } catch {
        /* ignore */
      }
    }
  });

  if (!user || !stats || !level) return null;

  return (
    <Link
      to="/app/motivasyon"
      data-testid="motivation-chip"
      className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors"
      title={`${level.level} · ${stats.xp} XP`}
    >
      <Trophy className="h-3.5 w-3.5 text-amber-600" strokeWidth={2} />
      <span data-testid="motivation-chip-xp">{stats.xp}</span>
      <span className="text-amber-400">·</span>
      <Flame className="h-3.5 w-3.5 text-red-500" strokeWidth={2} />
      <span data-testid="motivation-chip-streak">{stats.streak_days}</span>
    </Link>
  );
}
